/**
 * What an event row calls the person it is about.
 *
 * The backend answers two things: `person_type`, the CATEGORY the UI filters on (resident, staff,
 * visitor…), and `person_role`, the POSITION it resolved for them — "Proprietário", "Inquilino",
 * "Responsável". The position is the useful one: "Morador" or "Funcionário" alone tells a porteiro
 * nothing about who walked in.
 *
 * Both surfaces carried their own copy of the category map and disagreed about the position: the
 * operational panel preferred `person_role`, the Eventos page ignored it. So the same event read
 * "Proprietário" on one screen and "Morador" on the other — and for an empresa, where the backend
 * now resolves the DEPARTMENT position (services/event_person_label.go), the page would have gone
 * on showing "Funcionário" for every single person.
 *
 * See eventPersonLabel.test.ts.
 */

const PERSON_TYPE_LABELS: Record<string, string> = {
  morador: 'Morador',
  proprietario: 'Proprietário',
  resident: 'Morador',
  visitante: 'Visitante',
  visitor: 'Visitante',
  funcionario: 'Funcionário',
  staff: 'Funcionário',
  prestador: 'Prestador',
  service_provider: 'Prestador',
  delivery: 'Entregador',
  entregador: 'Entregador',
  lpr_access: 'Acesso LPR',
};

/** The category, in Portuguese. An unknown one is shown as stored rather than dropped. */
export function eventPersonTypeLabel(personType?: string | null): string | null {
  const key = (personType ?? '').trim().toLowerCase();
  if (!key) return null;
  return PERSON_TYPE_LABELS[key] ?? personType ?? null;
}

/** An event as either surface holds it: the page keeps the API's snake_case, the panel camel-cases. */
export interface EventPersonFields {
  person_role?: string | null;
  person_type?: string | null;
  personRole?: string | null;
  personType?: string | null;
}

/**
 * The label to show: the resolved position when there is one, the category otherwise.
 *
 * Null for an event about no person — a vehicle event or an unknown tag — so the column stays
 * empty instead of inventing a category.
 */
export function eventPersonLabel(event?: EventPersonFields | null): string | null {
  if (!event) return null;
  const role = (event.person_role ?? event.personRole ?? '').trim();
  if (role) return role;
  return eventPersonTypeLabel(event.person_type ?? event.personType);
}
